import { createElement, useEffect, useRef, useState } from "react";

const FAILSAFE_MS = 1200;
const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const inViewport = (el) => {
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
};

/**
 * Scroll reveal: fade + slide up (~500ms) the moment the element enters the viewport.
 * Starts hidden (unless reduced motion), reveals via IntersectionObserver with a
 * scroll/resize rect check and a 1200ms in-view fail-safe so nothing can stay hidden.
 */
export const Reveal = ({ children, delay = 0, className = "", as = "div", style, ...rest }) => {
  const ref = useRef(null);
  const [hidden, setHidden] = useState(() => !reduceMotion() && typeof IntersectionObserver !== "undefined");
  const [instant, setInstant] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hidden) return undefined;

    let done = false;
    const show = (immediate = false) => {
      if (done) return;
      done = true;
      if (immediate) setInstant(true);
      setHidden(false);
    };

    const observer = new IntersectionObserver(
      (entries) => entries.some((e) => e.isIntersecting) && show(),
      { threshold: 0 }
    );
    observer.observe(el);

    // Fallbacks: a rect check on scroll/resize, and a hard check after 1200ms in view.
    let raf = 0;
    const check = () => {
      raf = 0;
      if (inViewport(el)) show();
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(check);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const timer = window.setTimeout(() => inViewport(el) && show(true), FAILSAFE_MS);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [hidden]);

  return createElement(
    as,
    {
      ref,
      className: `reveal ${className}`.trim(),
      "data-hidden": hidden ? "true" : undefined,
      "data-instant": instant ? "true" : undefined,
      style: { ...style, transitionDelay: hidden || instant ? undefined : `${delay}ms` },
      ...rest,
    },
    children
  );
};
